import { locales } from '@ardoq/locale';
import libPluralize from 'pluralize';

export enum PluralOperand {
  ZERO = 'zero',
  ONE = 'one',
  TWO = 'two',
  FEW = 'few',
  MANY = 'many',
  OTHER = 'other',
}
export type LocalizedTextPlural = {
  [PluralOperand.ZERO]?: string;
  [PluralOperand.ONE]: string;
  [PluralOperand.TWO]?: string;
  [PluralOperand.FEW]?: string;
  [PluralOperand.MANY]?: string;
  [PluralOperand.OTHER]: string;
};

export enum CommaStyle {
  OXFORD = 'OXFORD',
  CAMBRIDGE = 'CAMBRIDGE',
}

export enum LastItemConjuction {
  AND = 'and',
  OR = 'or',
}

export const ensureString = (str: unknown): string =>
  str !== null && str !== undefined ? String(str) : '';

export const escapeJavascriptColon = (str: any) =>
  ensureString(str).replace(/javascript:/gi, 'javascript%3');

export const ensureAbsolutePathUrl = (url: string) =>
  url.includes('://') || url.startsWith('//') ? url : `//${url}`;

/**
 *
 * @param value input string
 * @param length length of the output string
 * @returns truncated input string
 */
export const truncate = (value: string, length = 200) =>
  value.length > length ? `${value.substring(0, length)}...` : value;

/**
 *
 * @param str input string
 * @returns capitalized output string
 */
export const capitalize = (str: string) =>
  str.slice(0, 1).toUpperCase() + str.slice(1);

/**
 *
 * @param {string} word - thing to pluralize
 * @param {string} count - number of things
 * @example pluralize('cat', 2) // 'cats'
 * @returns pluralized word if count > 1
 */
export const pluralize = (word: string, count: number) =>
  libPluralize(word, count);

/**
 *
 * @param {string} word - thing to pluralize
 * @param {string} count - number of things
 * @example pluralizeWithCount('cat', 2) // '2 cats'
 * @returns pluralized word if count > 1, and prefixes with the count
 */
export const pluralizeWithCount = (word: string, count: number) =>
  libPluralize(word, count, true);

export const unpluralizeVerb = (verb: string, count: number) =>
  count === 1 ? `${verb}s` : verb;

/**
 *
 * @param input string
 * @returns boolean hasOnlyWhiteCharacters
 */
export const getHasOnlyWhiteCharacters = (str: string) =>
  (typeof str === 'string' && str.trim() === '') || false;

/**
 * Selects between 2 different styles of serial comma use.
 * https://en.wikipedia.org/wiki/Serial_comma
 * @param numberOfListedItems number
 * @returns string
 */
const pickCommaStyle = (
  numberOfListedItems: number,
  conjunction: string,
  commaStyle: CommaStyle
): string => {
  if (numberOfListedItems <= 2) return ` ${conjunction}`;
  switch (commaStyle) {
    case CommaStyle.CAMBRIDGE:
      return ` ${conjunction},`;
    case CommaStyle.OXFORD:
    default:
      return `, ${conjunction}`;
  }
};

/**
 * Formats a list of items with the right number of commas, it uses `and` for the last
 * item and Oxford comma when needed.
 * @param inputList string[]
 * @returns string
 */
export const formatListAsEnglishProse = (
  inputList: string[],
  conjunction = LastItemConjuction.AND,
  commaStyle = CommaStyle.OXFORD
): string => {
  if (inputList.length === 0) return '';
  if (inputList.length === 1) return inputList[0];
  const innerCopy = inputList.slice();
  const maybeConjunctionWithComma = pickCommaStyle(
    innerCopy.length,
    conjunction,
    commaStyle
  );
  const last = innerCopy.pop();
  return `${innerCopy.join(', ')}${maybeConjunctionWithComma} ${last}`;
};

/**
 * Given a string with interpolation like this `{example}` and an object of key values,
 * where the keys match the interpolation values, replace them with the value of the object.
 * @param message string
 * @param interpolations Record<string, string>
 * @returns string
 */
export const interpolate = (
  message: string,
  interpolations: Record<string, string>
): string =>
  Object.entries(interpolations).reduce(
    (interpolated, [key, value]) =>
      interpolated.replace(new RegExp(`{*${key}*}`, 'g'), value),
    message
  );

/**
 * Using the build-in PluralRules, picks the right plural form from an object of options.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules
 * @param options LocalizedTextPlural
 * @param count number
 * @param locale string
 * @returns string
 */
export const pickCorrectPlural = (
  options: LocalizedTextPlural,
  count: number,
  locale = locales.enGB
): string => {
  const operand = new Intl.PluralRules(locale.code).select(count);
  return options[operand] || options[PluralOperand.ONE];
};
export const tokenIsString = (token?: string): token is string =>
  typeof token === 'string';

/**
 * Given a string, returns the first letter of the first and last word.
 * @param name string | undefined
 * @returns string
 */
export const getInitials = (name: string | undefined) => {
  if (!name) return '';
  const initials = name
    .trim()
    .split(' ')
    .map(part => part.charAt(0));
  const first = initials.at(0);
  const last = initials.length > 1 ? initials.at(-1) : null;
  return `${first ?? ''}${last ?? ''}`;
};

export const caseInsensitiveStringIncludes = (
  stringToSearch: string,
  searchString: string
) => stringToSearch.toLowerCase().includes(searchString.toLowerCase());
